import { useEffect, useRef, useState } from 'react';

import { DropdownFormMultipleChoices, DropdownFormSingleChoice } from '@/shared-components/dropdown';
import FormError from '@/shared-components/error/components/FormError';
import CalendarIcon from '@/shared-components/icons/components/forms/CalendarIcon';
import PasswordEyeClosed from '@/shared-components/icons/components/forms/PasswordEyeClosed';
import PasswordEyeOpen from '@/shared-components/icons/components/forms/PasswordEyeOpen';
import InfoIconArtwork from '@/shared-components/icons/components/public/InfoIconArtwork';

import FormCheckbox from './FormCheckbox';
import TextToggle from './TextToggle';

import './styles/PublicFormInput.scss';

export default function PublicFormInput({
  className = null,
  placeholder = "",
  type = "text",
  onChange = null,
  onKeyDown = null,
  margin = false,
  element = "",
  name = null,
  inputMode = null,
  min = null,
  max = null,
  step = null,
  maxLength = null,
  rows = null,
  autoComplete = undefined,
  value,
  fillWith,
  reset,
  error,
  readOnly = false,
  children,
  selectOptions,
  optionA = "",
  optionAText = "",
  optionB,
  optionBText = "",
  fixedPlaceholder,
  secondaryColor = false,
  headerInfoIcon = "",
  infoIconHeader = "",
  infoIconDescription = "",
  headerInfoIcon2 = "",
  infoIconDescription2 = "",
  currency = null,
  autoResize = false, // new optional prop, defaults to false
  maxRows = null      // new optional prop to limit the number of rows, defaults to null
}) {
  // Only used for type password
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef();
  const dateRef = useRef();
  const [isInfoIconOpen, setIsInfoIconOpen] = useState(false);

  /*   const handleInfoIconClick = (e) => {
    e.stopPropagation();                     // detiene propagación DOM
    e.nativeEvent.stopImmediatePropagation(); // por si hay más
    setIsInfoIconOpen(prev => !prev);
  }; */

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    if (reset === true) inputRef.current.value = "";
  }, [reset]);

  // Auto-resize effect for textareas when autoResize is true.
  useEffect(() => {
    if (type === "textarea" && autoResize && inputRef.current) {
      inputRef.current.style.height = "auto";
      const computedStyle = window.getComputedStyle(inputRef.current);
      let lineHeight = parseFloat(computedStyle.lineHeight);
      if (isNaN(lineHeight)) {
        // Fallback: assume 1.2 times the font size if lineHeight is "normal"
        const fontSize = parseFloat(computedStyle.fontSize);
        lineHeight = fontSize * 1.2;
      }
      const newHeight = inputRef.current.scrollHeight;
      const maxHeight = maxRows ? lineHeight * maxRows : newHeight;
      inputRef.current.style.height = Math.min(newHeight, maxHeight) + "px";
    }
  }, [value, autoResize, type, maxRows]);

  if (type === "checkbox") {
    return (
      <>
        <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
          <div className="public-form-input__checkbox-container">
            <FormCheckbox
              className="public-form-input__checkbox"
              element={element}
              readOnly={readOnly}
              value={value}
              onChange={onChange}
            />

            <p className="public-form-input__checkbox-text element-non-selectable">
              {children}
            </p>
          </div>

          <FormError
            error={error}
            alternativeElement={`${element}_error`}
            errorClassName="public-form-input__checkbox-error"
            element={element}
          />

          {margin && <div className="public-form-input__default-margin checkbox"/>}
          {className && <div className={className}></div>}
        </div>
      </>
    );
  }

  if (type === "select") {
    return (
      <>
        <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
          <DropdownFormSingleChoice
            element={element}
            selectOptions={selectOptions}
            readOnly={readOnly}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            autoComplete={autoComplete}
            infoIconDescription={infoIconDescription}
          />

          <FormError
            error={error}
            alternativeElement={`${element}_error`}
            errorClassName="public-form-input__input-error"
            element={element}
          />
        </div>
      </>
    );
  }

  if (type === "multiple") {
    return (
      <>
        <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
          <DropdownFormMultipleChoices
            element={element}
            selectOptions={selectOptions}
            readOnly={readOnly}
            placeholder={placeholder}
            fillWith={fillWith}
          />

          <FormError
            error={error}
            alternativeElement={`${element}_error`}
            errorClassName="public-form-input__input-error"
            element={element}
          />
        </div>
      </>
    );
  }

  if (type === "choose") {
    return (
      <>
        <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
          <TextToggle
            optionA={optionA}
            optionB={optionB}
            optionAText={optionAText}
            optionBText={optionBText}
            readOnly={readOnly}
            value={value}
            onChange={onChange}
            headerInfoIcon={headerInfoIcon === '' ? value : headerInfoIcon}
            infoIconDescription={infoIconDescription}
            headerInfoIcon2={headerInfoIcon2}
            infoIconDescription2={infoIconDescription2}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
        <div className="public-form-input__container" id={`${element}_error`}>
          { placeholder &&
            (<label
              htmlFor={element}
              className={`public-form-input__label${type==="textarea" ? " textarea" : ""}`}>
              {placeholder}
              {infoIconDescription.length > 0 &&
                <span
                  onClick={e => {          // ① fase CAPTURA, antes de About.jsx
                    e.stopPropagation();                // ② cancela toda la cadena
                    e.nativeEvent.stopImmediatePropagation(); // por si hay más
                    e.preventDefault(); // evita que se envíe el formulario
                    setIsInfoIconOpen(prev => !prev);   // ③ abrimos/cerramos ayuda
                  }}
                  role="button"
                  tabIndex={-1}
                >
                  <InfoIconArtwork
                    className="step-accordeon-step-header__info-icon"
                    open={isInfoIconOpen}
                    header={infoIconHeader || placeholder}
                    description={infoIconDescription}
                    onClick={() => setIsInfoIconOpen(false)}
                  />
                </span>
              }
            </label>)
          }
          {
            type === "textarea" ?
              <textarea
                className={`public-form-input__input textarea ${readOnly ? 'read-only' : ''}`}
                name={name ?? element}
                id={element}
                ref={inputRef}
                readOnly={readOnly}
                spellCheck={false}
                value={value}
                defaultValue={fillWith}
                onChange={onChange}
                onKeyDown={onKeyDown}
                rows={rows}
                maxLength={maxLength}
                autoComplete={autoComplete}
              />
              :
              type === "date" ?
                <>
                  <input
                    className="public-form-input__input date"
                    type={type}
                    name={name ?? element}
                    id={element}
                    ref={dateRef}
                    readOnly={readOnly}
                    value={value}
                    defaultValue={fillWith}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    autoComplete={autoComplete}
                  />

                  <CalendarIcon className={`public-form-input__calendar-icon ${readOnly ? "read-only" : ""}`} onClick={() => dateRef.current.showPicker()}/>
                </>
                : (type === "password" && !readOnly) ?
                  <>
                    <div className={`input-container ${secondaryColor ? "secondary-color" : ""}`}>
                      {fixedPlaceholder && (
                        <span className="static-prefix">{fixedPlaceholder}</span>
                      )}
                      <input
                        className="public-form-input__input"
                        type={showPassword ? "text" : "password"}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        name={name ?? element}
                        id={element}
                        ref={inputRef}
                        readOnly={readOnly}
                        spellCheck={false}
                        value={value}
                        defaultValue={fillWith}
                        inputMode={inputMode}
                        disabled={readOnly}
                        autoComplete={autoComplete}
                      />

                      {showPassword ? (
                        <PasswordEyeClosed className={`public-form-input__password-icon ${readOnly ? "read-only" : ""}`} onClick={togglePasswordVisibility}/>
                      ) : (
                        <PasswordEyeOpen className={`public-form-input__password-icon ${readOnly ? "read-only" : ""}`} onClick={togglePasswordVisibility}/>
                      )}
                    </div>
                  </>
                  : (type === "currencyInput" && !readOnly) ?
                    <>
                      <div className={`input-container ${secondaryColor ? "secondary-color" : ""}`}>
                        {fixedPlaceholder && (
                          <span className="static-prefix">{fixedPlaceholder}</span>
                        )}
                        <input
                          className="public-form-input__input"
                          type="number"
                          onChange={onChange}
                          onKeyDown={onKeyDown}
                          name={name ?? element}
                          id={element}
                          ref={inputRef}
                          readOnly={readOnly}
                          spellCheck={false}
                          value={value}
                          defaultValue={fillWith}
                          inputMode={inputMode}
                          disabled={readOnly}
                          min={min ?? undefined}
                          max={max ?? undefined}
                          step={step ?? undefined}
                          autoComplete={autoComplete}
                        />

                        {currency &&
                          <div className="public-form-input__currency">
                            { currency }
                          </div>
                        }
                      </div>
                    </>
                    :
                    <>
                      <div className={`input-container ${secondaryColor ? "secondary-color" : ""}`}>
                        {fixedPlaceholder && (
                          <span className="static-prefix">{fixedPlaceholder}</span>
                        )}
                        <input
                          className="public-form-input__input"
                          type={type}
                          onChange={onChange}
                          onKeyDown={onKeyDown}
                          name={name ?? element}
                          id={element}
                          ref={inputRef}
                          readOnly={readOnly}
                          spellCheck={false}
                          value={value}
                          defaultValue={fillWith}
                          inputMode={inputMode}
                          autoComplete={autoComplete}
                          {...(type === 'number' ? { min: (min ?? undefined), max: (max ?? undefined), step: (step ?? undefined) } : {})}
                        />
                      </div>
                    </>
          }
        </div>

        <FormError
          error={error}
          alternativeElement={`${element}_error`}
          errorClassName="public-form-input__input-error"
          element={element}
        />
      </div>
    </>
  );
}
